/**
 * Matrix Update Batcher
 * Permet de grouper les mises à jour de matrices pour éviter
 * de recalculer la hiérarchie multiple fois par frame
 */
class MatrixUpdateBatcher {
    constructor() {
        this.pendingUpdates = new Set();
        this.isScheduled = false;
        this.frameId = null;
    }

    /**
     * Ajoute un objet à la file d'attente de mise à jour
     * @param {Object3D} object - L'objet Three.js à mettre à jour
     */
    add(object) {
        this.pendingUpdates.add(object);
        this._scheduleUpdate();
    }

    /**
     * Planifie la mise à jour de toutes les matrices en batch
     * @private
     */
    _scheduleUpdate() {
        if (this.isScheduled) return;
        
        this.isScheduled = true;
        this.frameId = requestAnimationFrame(() => {
            this._flush();
        });
    }

    /**
     * Exécute toutes les mises à jour en attente
     * @private
     */
    _flush() {
        // Mettre à jour toutes les matrices en une seule passe
        this.pendingUpdates.forEach(object => {
            if (object && object.updateMatrixWorld) {
                object.updateMatrixWorld(true);
            }
        });
        
        this.pendingUpdates.clear();
        this.isScheduled = false;
        this.frameId = null;
    }

    /**
     * Force l'exécution immédiate des mises à jour en attente
     */
    flush() {
        if (this.frameId) {
            cancelAnimationFrame(this.frameId);
        }
        this._flush();
    }

    /**
     * Nettoie toutes les mises à jour en attente
     */
    clear() {
        if (this.frameId) {
            cancelAnimationFrame(this.frameId);
        }
        this.pendingUpdates.clear();
        this.isScheduled = false;
        this.frameId = null;
    }

    /**
     * Retourne le nombre de mises à jour en attente
     */
    getPendingCount() {
        return this.pendingUpdates.size;
    }
}

// Instance singleton
const matrixUpdateBatcher = new MatrixUpdateBatcher();

export default matrixUpdateBatcher;
export { MatrixUpdateBatcher };
